import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdmissionSettings } from '@pandacalcio/validation';
import { useLeague } from '../../features/leagues/api';
import { useAnnouncer } from '../../components/LiveAnnouncer';
import { usePageTitle } from '../../lib/usePageTitle';
import { AppLayout } from '../../components/AppLayout';
import { TextField } from '../../components/TextField';
import { Button } from '../../components/Button';
import { LeagueNav } from './LeagueNav';
import { api, ApiError } from '../../lib/api';

interface QuestionRow {
  id: string;
  question: string;
  options: string[];
  correctIndex: number;
}

/** Gestione ammissione (admin): requisiti, quiz e banca domande (lineeguida §22–§23). */
export function AdmissionAdminPage() {
  const { id = '' } = useParams();
  const league = useLeague(id);
  usePageTitle('Ammissione');
  const { announce } = useAnnouncer();
  const queryClient = useQueryClient();

  const admission = useQuery({
    queryKey: ['leagues', id, 'admission'],
    queryFn: () =>
      api<{ admission: AdmissionSettings; questionBankSize: number }>(`/leagues/${id}/admission`),
    enabled: !!id,
  });
  const questions = useQuery({
    queryKey: ['leagues', id, 'quiz-questions'],
    queryFn: () => api<{ questions: QuestionRow[] }>(`/leagues/${id}/quiz/questions`).then((r) => r.questions),
    enabled: !!id,
  });

  const [draft, setDraft] = useState<AdmissionSettings | null>(null);
  const [newRequirement, setNewRequirement] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (admission.data && !draft) setDraft(structuredClone(admission.data.admission));
  }, [admission.data, draft]);

  const isAdmin = league.data?.myRole === 'ADMIN' || league.data?.myRole === 'SUPER_ADMIN';
  if (league.data && !isAdmin) {
    return (
      <AppLayout title="Ammissione">
        <p role="alert" className="mt-4 font-medium text-danger">
          Solo l’amministratore della lega può gestire l’ammissione.
        </p>
      </AppLayout>
    );
  }

  const save = async () => {
    if (!draft) return;
    setError(null);
    setSaving(true);
    try {
      const res = await api<{ message: string }>(`/leagues/${id}/admission`, {
        method: 'PUT',
        body: draft,
      });
      announce(res.message);
      queryClient.invalidateQueries({ queryKey: ['leagues', id, 'admission'] });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Salvataggio non riuscito.';
      setError(msg);
      announce(`Errore: ${msg}`, 'assertive');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout title={league.data ? `Ammissione — ${league.data.name}` : 'Ammissione'}>
      <LeagueNav leagueId={id} />

      {error && (
        <p role="alert" className="mt-4 rounded-lg border-2 border-danger bg-red-50 p-3 font-medium text-danger dark:bg-ink-800">
          {error}
        </p>
      )}

      {draft && (
        <>
          {/* Requisiti testuali */}
          <section aria-labelledby="adm-req" className="mt-6 max-w-xl">
            <h2 id="adm-req" className="text-2xl font-bold tracking-tight">Requisiti di ammissione</h2>
            <p className="mt-1 text-ink-700 dark:text-ink-200">
              Mostrati a chi riceve un invito. Esempio: “Almeno 3 ingressi allo stadio Del Duca”.
            </p>
            {draft.requirements.length > 0 && (
              <ul className="mt-3 flex flex-col gap-2">
                {draft.requirements.map((r, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 rounded-xl border border-ink-200 px-3 py-2 dark:border-ink-800">
                    <span>{r}</span>
                    <Button
                      variant="secondary"
                      onClick={() =>
                        setDraft({ ...draft, requirements: draft.requirements.filter((_, j) => j !== i) })
                      }
                    >
                      Rimuovi
                      <span className="visually-hidden"> il requisito {r}</span>
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <form
              noValidate
              className="mt-3 flex items-end gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (newRequirement.trim().length < 3) return;
                setDraft({ ...draft, requirements: [...draft.requirements, newRequirement.trim()] });
                setNewRequirement('');
                announce('Requisito aggiunto (ricorda di salvare).');
              }}
            >
              <div className="flex-1">
                <TextField
                  label="Nuovo requisito"
                  value={newRequirement}
                  onChange={(e) => setNewRequirement(e.target.value)}
                />
              </div>
              <Button type="submit" variant="secondary">
                Aggiungi
              </Button>
            </form>
          </section>

          {/* Impostazioni quiz */}
          <section aria-labelledby="adm-quiz" className="mt-8 max-w-xl">
            <h2 id="adm-quiz" className="text-2xl font-bold tracking-tight">Quiz di ammissione</h2>
            <label className="mt-3 flex min-h-11 cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                className="h-5 w-5 accent-panda-700"
                checked={draft.quiz.enabled}
                onChange={(e) => setDraft({ ...draft, quiz: { ...draft.quiz, enabled: e.target.checked } })}
              />
              <span className="font-medium">
                Attiva il quiz (chi entra deve superarlo prima di diventare membro)
              </span>
            </label>
            {admission.data && (
              <p className="mt-1 text-sm text-ink-600 dark:text-ink-300">
                Banca domande: {admission.data.questionBankSize} domande attive.
              </p>
            )}
            <div className="mt-3 flex flex-wrap gap-4">
              {(
                [
                  ['Domande per tentativo', 'questionCount'],
                  ['Risposte corrette minime', 'minCorrect'],
                  ['Tempo massimo (minuti)', 'timeLimitMinutes'],
                  ['Tentativi massimi', 'maxAttempts'],
                ] as Array<[string, keyof AdmissionSettings['quiz'] & string]>
              ).map(([label, key]) => (
                <label key={key} className="flex flex-col gap-1">
                  <span className="font-medium">{label}</span>
                  <input
                    type="number"
                    min={1}
                    max={120}
                    className="w-28 rounded-lg border border-ink-300 px-3 py-2 dark:border-ink-600 dark:bg-ink-900"
                    value={draft.quiz[key] as number}
                    onChange={(e) =>
                      setDraft({ ...draft, quiz: { ...draft.quiz, [key]: Number(e.target.value) } })
                    }
                  />
                </label>
              ))}
            </div>
          </section>

          <div className="mt-6">
            <Button onClick={save} loading={saving} loadingText="Salvataggio…">
              Salva impostazioni
            </Button>
          </div>
        </>
      )}

      {/* Banca domande */}
      <QuestionBank leagueId={id} questions={questions.data ?? []} />
    </AppLayout>
  );
}

function QuestionBank({ leagueId, questions }: { leagueId: string; questions: QuestionRow[] }) {
  const { announce } = useAnnouncer();
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [options, setOptions] = useState(['', '', '', '']);
  const [correctIndex, setCorrectIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const addQuestion = async () => {
    setError(null);
    const filled = options.map((o) => o.trim()).filter(Boolean);
    if (text.trim().length < 5) {
      setError('Scrivi la domanda.');
      return;
    }
    if (filled.length < 2) {
      setError('Servono almeno 2 risposte.');
      return;
    }
    if (correctIndex >= filled.length) {
      setError('Segna quale risposta è quella corretta.');
      return;
    }
    setSaving(true);
    try {
      await api(`/leagues/${leagueId}/quiz/questions`, {
        method: 'POST',
        body: { question: text.trim(), options: filled, correctIndex },
      });
      announce('Domanda aggiunta alla banca.');
      setText('');
      setOptions(['', '', '', '']);
      setCorrectIndex(0);
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'quiz-questions'] });
      queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'admission'] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Salvataggio non riuscito.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section aria-labelledby="banca-domande" className="mt-10 max-w-2xl">
      <h2 id="banca-domande" className="text-2xl font-bold tracking-tight">
        Banca domande ({questions.length})
      </h2>

      {questions.length > 0 && (
        <ul className="mt-4 flex flex-col divide-y divide-ink-200 rounded-2xl border border-ink-200 dark:divide-ink-800 dark:border-ink-800">
          {questions.map((q, qi) => (
            <li key={q.id} className="flex flex-col gap-1 px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <p className="font-semibold">
                  {qi + 1}. {q.question}
                </p>
                <Button
                  variant="secondary"
                  onClick={async () => {
                    await api(`/leagues/${leagueId}/quiz/questions/${q.id}`, { method: 'DELETE' });
                    announce('Domanda rimossa.');
                    queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'quiz-questions'] });
                    queryClient.invalidateQueries({ queryKey: ['leagues', leagueId, 'admission'] });
                  }}
                >
                  Rimuovi
                  <span className="visually-hidden"> la domanda {qi + 1}</span>
                </Button>
              </div>
              <ul className="flex flex-col gap-0.5 pl-4 text-ink-700 dark:text-ink-200">
                {q.options.map((o, oi) => (
                  <li key={oi}>
                    {oi === q.correctIndex ? (
                      <strong>
                        ✓ {o} <span className="visually-hidden">(risposta corretta)</span>
                      </strong>
                    ) : (
                      o
                    )}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}

      <form
        noValidate
        className="mt-6 flex flex-col gap-4 rounded-2xl border border-ink-200 p-5 dark:border-ink-800"
        onSubmit={(e) => {
          e.preventDefault();
          addQuestion();
        }}
      >
        <h3 className="text-lg font-bold">Nuova domanda</h3>
        {error && (
          <p role="alert" className="font-medium text-danger">{error}</p>
        )}
        <TextField label="Domanda" required value={text} onChange={(e) => setText(e.target.value)} />
        <fieldset>
          <legend className="font-medium">
            Risposte (seleziona quella corretta) <span className="text-danger">*</span>
          </legend>
          <div className="mt-2 flex flex-col gap-2">
            {options.map((option, i) => (
              <div key={i} className="flex items-center gap-3">
                <input
                  type="radio"
                  name="correct"
                  aria-label={`La risposta ${i + 1} è quella corretta`}
                  className="h-5 w-5 accent-panda-700"
                  checked={correctIndex === i}
                  onChange={() => setCorrectIndex(i)}
                />
                <input
                  aria-label={`Testo risposta ${i + 1}`}
                  className="flex-1 rounded-lg border border-ink-300 px-3 py-2 dark:border-ink-600 dark:bg-ink-900"
                  value={option}
                  onChange={(e) => setOptions(options.map((o, j) => (j === i ? e.target.value : o)))}
                />
              </div>
            ))}
          </div>
        </fieldset>
        <div>
          <Button type="submit" loading={saving} loadingText="Salvataggio…">
            Aggiungi domanda
          </Button>
        </div>
      </form>
    </section>
  );
}
